import axios from "axios";

// API Configuration
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:5000/api";
const REQUEST_TIMEOUT = 10000; // 10 seconds

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token and handle loading states
api.interceptors.request.use(
  config => {
    // Add auth token if available
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling and logging
api.interceptors.response.use(
  response => {
    // Log response time for debugging
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;

    if (import.meta.env.DEV) {
      // console.log(
      //   `API Request: ${response.config.method?.toUpperCase()} ${
      //     response.config.url
      //   } - ${duration}ms`
      // );
    }

    return response;
  },
  error => {
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem("token");
          localStorage.removeItem("user");

          // Only redirect if not already on login page
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
          break;

        case 403:
          // Forbidden - user doesn't have permission
          console.error("Access forbidden:", data?.error?.message);
          break;

        case 404:
          // Not found
          console.error("Resource not found:", error.config.url);
          break;

        case 422:
          // Validation error
          console.error("Validation error:", data?.error?.details);
          break;

        case 429:
          // Rate limit exceeded
          console.error("Rate limit exceeded. Please try again later.");
          break;

        case 500:
          // Server error
          console.error("Server error:", data?.error?.message);
          break;

        default:
          console.error("API Error:", status, data?.error?.message);
      }

      // Enhance error with user-friendly message
      const enhancedError = new Error(
        data?.error?.message ||
          data?.message ||
          `Request failed with status ${status}`,
      );
      enhancedError.status = status;
      enhancedError.code = data?.error?.code;
      enhancedError.details = data?.error?.details;

      return Promise.reject(enhancedError);
    } else if (error.request) {
      // Network error or no response
      const networkError = new Error(
        "Network error. Please check your internet connection and try again.",
      );
      networkError.isNetworkError = true;
      return Promise.reject(networkError);
    } else {
      // Request setup error
      return Promise.reject(error);
    }
  },
);

// API helper functions
export const apiHelpers = {
  // Check if error is a network error
  isNetworkError: error => error.isNetworkError === true,

  // Check if error is a validation error
  isValidationError: error => error.status === 422,

  // Check if error is an auth error
  isAuthError: error => error.status === 401 || error.status === 403,

  // Get user-friendly error message
  getErrorMessage: error => {
    if (apiHelpers.isNetworkError(error)) {
      return "Unable to connect to the server. Please check your internet connection.";
    }

    if (apiHelpers.isValidationError(error)) {
      return error.details
        ? Object.values(error.details).flat().join(", ")
        : error.message;
    }

    return error.message || "An unexpected error occurred. Please try again.";
  },

  // Retry failed requests
  retryRequest: async (originalRequest, maxRetries = 3) => {
    let retries = 0;

    while (retries < maxRetries) {
      try {
        return await api(originalRequest);
      } catch (error) {
        retries++;

        // Don't retry auth errors or validation errors
        if (
          apiHelpers.isAuthError(error) ||
          apiHelpers.isValidationError(error)
        ) {
          throw error;
        }

        // Don't retry if we've reached max retries
        if (retries >= maxRetries) {
          throw error;
        }

        // Wait before retrying (exponential backoff)
        await new Promise(resolve =>
          setTimeout(resolve, Math.pow(2, retries) * 1000),
        );
      }
    }
  },
};

export default api;
